export interface BrowserNotificationInstance {
  onclick: (() => void) | null;
  close(): void;
}

export interface BrowserNotificationConstructor {
  readonly permission: NotificationPermission;
  requestPermission(): Promise<NotificationPermission>;
  new (
    title: string,
    options?: NotificationOptions,
  ): BrowserNotificationInstance;
}

export interface DesktopNotificationInput {
  title: string;
  groupKey: string;
  preview?: string;
  showPreview: boolean;
  onOpen(): void;
}

export interface DesktopNotificationChannel {
  permission(): NotificationPermission | 'unsupported';
  requestPermission(): Promise<NotificationPermission | 'unsupported'>;
  show(input: DesktopNotificationInput): void;
}

interface DesktopNotificationChannelOptions {
  notificationConstructor?: BrowserNotificationConstructor | null;
  focusWindow?: () => void;
}

const safelyRun = (action: () => void) => {
  try {
    action();
  } catch {
    return;
  }
};

const defaultNotificationConstructor = () =>
  typeof Notification === 'undefined'
    ? null
    : (Notification as BrowserNotificationConstructor);

export const createDesktopNotificationChannel = (
  options: DesktopNotificationChannelOptions = {},
): DesktopNotificationChannel => {
  const notificationConstructor =
    options.notificationConstructor === undefined
      ? defaultNotificationConstructor()
      : options.notificationConstructor;
  const focusWindow = options.focusWindow ?? (() => globalThis.window?.focus());

  const permission = () => notificationConstructor?.permission ?? 'unsupported';

  const requestPermission = async () => {
    if (!notificationConstructor) return 'unsupported' as const;
    try {
      return await notificationConstructor.requestPermission();
    } catch {
      return 'unsupported' as const;
    }
  };

  const show = (input: DesktopNotificationInput) => {
    if (!notificationConstructor || permission() !== 'granted') return;
    try {
      const notification = new notificationConstructor(input.title, {
        tag: input.groupKey,
        ...(input.showPreview && input.preview ? { body: input.preview } : {}),
      });
      notification.onclick = () => {
        safelyRun(() => notification.close());
        safelyRun(focusWindow);
        safelyRun(input.onOpen);
      };
    } catch {
      return;
    }
  };

  return { permission, requestPermission, show };
};
